/**
 * ErrorFallback - Reusable fallback UI for error boundaries
 *
 * Displays a user-friendly error message with recovery options.
 * Supports bilingual display (Swedish primary, English secondary).
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';

export interface ErrorFallbackProps {
  /** The error that was caught */
  error: Error;
  /** Callback to reset the error boundary and retry */
  resetError?: () => void;
  /** Optional custom title */
  title?: string;
  /** Whether to show a compact version (for chart/component errors) */
  compact?: boolean;
}

/**
 * Full-size error fallback for page-level errors
 *
 * Note: This component intentionally does NOT use useNavigate() hook
 * because it may be rendered outside of Router context when error
 * boundaries catch errors during initial render. Uses window.location
 * for navigation instead.
 */
export function ErrorFallback({
  error,
  resetError,
  title,
  compact = false,
}: ErrorFallbackProps): React.ReactElement {
  const handleGoHome = () => {
    // Use window.location instead of useNavigate() to avoid
    // "useNavigate() may be used only in the context of a <Router> component" error
    // when error boundary catches errors before Router is mounted
    window.location.href = '/';
  };

  if (compact) {
    return (
      <div className="flex min-h-[200px] flex-col items-center justify-center rounded-lg border border-border bg-muted/50 p-4 text-center">
        <AlertTriangle className="mb-2 h-8 w-8 text-destructive" />
        <p className="mb-3 text-sm text-muted-foreground">Det gick inte att ladda komponenten</p>
        {resetError && (
          <Button variant="outline" size="sm" onClick={resetError}>
            <RefreshCw className="mr-1 h-3 w-3" />
            Försök igen
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">{title || 'Något gick fel'}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-3 text-center">
          <p className="text-muted-foreground">
            Vi kunde inte ladda den här sidan. Vänligen försök igen eller gå tillbaka till
            startsidan.
          </p>
          <p className="text-sm text-muted-foreground">
            Something went wrong. Please try again or return to the homepage.
          </p>

          {import.meta.env.DEV && (
            <details className="mt-4 text-left">
              <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                Tekniska detaljer (development only)
              </summary>
              <pre className="mt-2 max-h-32 overflow-auto rounded-md bg-muted p-3 text-xs">
                {error.message}
                {error.stack && (
                  <>
                    {'\n\n'}
                    {error.stack}
                  </>
                )}
              </pre>
            </details>
          )}
        </CardContent>

        <CardFooter className="flex justify-center gap-3">
          {resetError && (
            <Button variant="outline" onClick={resetError}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Försök igen
            </Button>
          )}
          <Button onClick={handleGoHome}>
            <Home className="mr-2 h-4 w-4" />
            Till startsidan
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

export default ErrorFallback;
